import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Send, CreditCard, Eye, Mail, MailOpen, PenSquare, ExternalLink, Download, TrendingUp, Target, Trophy, Beaker, Copy, X as XIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import ProposalSendModal from "@/components/admin/ProposalSendModal";
import EmailHistoryPanel from "@/components/admin/EmailHistoryPanel";

const STATUS_LABEL = {
  brouillon: { l: "Brouillon", c: "bg-slate-200 text-slate-700" },
  envoye: { l: "Envoyé", c: "bg-[#67E8F9] text-[#0A1128]" },
  negociation: { l: "Négociation", c: "bg-[#FFDCA8] text-[#92400E]" },
  signe: { l: "Signé", c: "bg-[#A7F3D0] text-[#065F46]" },
  paye_acompte: { l: "Acompte payé", c: "bg-[#FFDCA8] text-[#92400E]" },
  en_cours: { l: "En cours", c: "bg-[#06B6D4] text-white" },
  livre: { l: "Livré", c: "bg-blue-500 text-white" },
  paye_solde: { l: "Soldé", c: "bg-green-600 text-white" },
  cloture: { l: "Clôturé", c: "bg-slate-500 text-white" },
  refuse: { l: "Refusé", c: "bg-red-500 text-white" },
  expire: { l: "Expiré", c: "bg-slate-300 text-slate-700" },
};

const SIGNED_STATUSES = new Set(["signe", "paye_acompte", "en_cours", "livre", "paye_solde", "cloture"]);

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) +
      " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function fmtEur(n) {
  return Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €";
}

export default function AdminProposals() {
  const [items, setItems] = useState([]);
  const [sendingProp, setSendingProp] = useState(null);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("actifs"); // actifs | signes

  const load = async () => {
    try {
      const r = await api.get("/admin/proposals");
      setItems(r.data);
      setActive((prev) => prev ? r.data.find((p) => p.id === prev.id) || r.data[0] || null : r.data[0] || null);
    } catch { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { load(); }, []);

  const openSend = (p) => setSendingProp(p);

  const markPaid = async (p, mode) => {
    if (!window.confirm(`Marquer ${mode === "deposit" ? "l'acompte" : mode === "balance" ? "le solde" : "le paiement intégral"} comme reçu par virement ?`)) return;
    try { await api.post(`/admin/proposals/${p.id}/mark-paid`, { mode, method: "virement" }); toast.success("Statut + facture mis à jour"); load(); } catch { toast.error("Erreur"); }
  };

  // Pipeline stats — based on PENDING devis (not signed, not refused)
  const stats = useMemo(() => {
    const pending = items.filter((p) => !SIGNED_STATUSES.has(p.status) && p.status !== "refuse" && p.status !== "expire");
    const totalTtc = pending.reduce((s, p) => s + Number((p.totals || {}).total_ttc || 0), 0);
    const totalPond = pending.reduce((s, p) => s + Number((p.totals || {}).total_ttc || 0) * (Number(p.probability || 0) / 100), 0);
    const high60 = pending.filter((p) => Number(p.probability || 0) >= 60);
    const high90 = pending.filter((p) => Number(p.probability || 0) >= 90);
    const sum60 = high60.reduce((s, p) => s + Number((p.totals || {}).total_ttc || 0), 0);
    const sum90 = high90.reduce((s, p) => s + Number((p.totals || {}).total_ttc || 0), 0);
    const signedCount = items.filter((p) => SIGNED_STATUSES.has(p.status)).length;
    return {
      pendingCount: pending.length,
      totalTtc,
      totalPond,
      sum60,
      count60: high60.length,
      sum90,
      count90: high90.length,
      signedCount,
    };
  }, [items]);

  const isExpired = (p) => {
    if (p.status === "expire") return true;
    if (!p.expires_at && !p.valid_until) return false;
    const exp = new Date(p.expires_at || p.valid_until);
    return !isNaN(exp.getTime()) && exp.getTime() < Date.now() && !SIGNED_STATUSES.has(p.status);
  };

  const counts = useMemo(() => ({
    actifs:  items.filter((p) => !SIGNED_STATUSES.has(p.status) && p.status !== "refuse" && !isExpired(p)).length,
    signes:  items.filter((p) => SIGNED_STATUSES.has(p.status)).length,
    archives: items.filter((p) => p.status === "refuse" || isExpired(p)).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    if (tab === "signes") return items.filter((p) => SIGNED_STATUSES.has(p.status));
    if (tab === "archives") return items.filter((p) => p.status === "refuse" || isExpired(p));
    return items.filter((p) => !SIGNED_STATUSES.has(p.status) && p.status !== "refuse" && !isExpired(p));
  }, [items, tab]);

  // If active is not in filtered list, switch to first available
  useEffect(() => {
    if (filteredItems.length === 0) { setActive(null); return; }
    if (!active || !filteredItems.find((p) => p.id === active.id)) setActive(filteredItems[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, items]);

  const pdfUrl = active?.public_token
    ? `${(typeof window !== "undefined" ? window.location.origin : "")}/api/proposals/public/${active.public_token}/pdf?v=${active.updated_at || active.id}`
    : null;

  return (
    <div data-testid="admin-proposals">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Devis & propositions</h1>
          <p className="text-sm text-[#64748B] mt-1">Pipeline en haut, devis & aperçu PDF en bas. Cliquez une ligne pour la visualiser.</p>
        </div>
        <Link to="/admin/devis/nouveau" data-testid="prop-new-btn">
          <Button className="bg-[#0A1128] hover:bg-[#14213d] text-white"><Plus size={16} className="mr-2" />Nouveau devis</Button>
        </Link>
      </div>

      {/* Pipeline stats */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="prop-pipeline-stats">
        <StatCard
          tone="navy"
          icon={TrendingUp}
          label="Pipeline total"
          value={fmtEur(stats.totalTtc)}
          sub={`${stats.pendingCount} devis en attente`}
          testId="prop-stat-total"
        />
        <StatCard
          tone="cyan"
          icon={Target}
          label="Pondéré"
          value={fmtEur(stats.totalPond)}
          sub="Σ TTC × probabilité"
          testId="prop-stat-weighted"
        />
        <StatCard
          tone="amber"
          icon={Target}
          label="≥ 60 %"
          value={fmtEur(stats.sum60)}
          sub={`${stats.count60} devis chauds`}
          testId="prop-stat-60"
        />
        <StatCard
          tone="green"
          icon={Trophy}
          label="≥ 90 %"
          value={fmtEur(stats.sum90)}
          sub={`${stats.count90} quasi signés`}
          testId="prop-stat-90"
        />
      </div>

      {/* Tabs */}
      <div className="mt-5 inline-flex items-center gap-1 bg-white border border-black/8 rounded-xl p-1" data-testid="prop-tabs">
        <button
          onClick={() => setTab("actifs")}
          data-testid="prop-tab-actifs"
          className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === "actifs" ? "bg-[#0A1128] text-white" : "text-[#64748B] hover:bg-black/5"}`}
        >
          Actifs ({counts.actifs})
        </button>
        <button
          onClick={() => setTab("signes")}
          data-testid="prop-tab-signes"
          className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === "signes" ? "bg-[#06B6D4] text-[#0A1128]" : "text-[#64748B] hover:bg-black/5"}`}
        >
          Signés ({counts.signes})
        </button>
        <button
          onClick={() => setTab("archives")}
          data-testid="prop-tab-archives"
          className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === "archives" ? "bg-slate-300 text-slate-800" : "text-[#64748B] hover:bg-black/5"}`}
        >
          Expirés / Refusés ({counts.archives})
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: table */}
        <div className="bg-white border border-black/8 rounded-2xl overflow-hidden lg:max-h-[calc(100vh-360px)] lg:overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="text-sm text-[#64748B] p-8 text-center">{tab === "signes" ? "Aucun devis signé pour l'instant." : tab === "archives" ? "Aucun devis expiré ou refusé." : "Aucun devis actif. Créez-en un !"}</div>
          ) : (
            <table className="w-full text-sm" data-testid="prop-table">
              <thead className="bg-[#FAFAFA] border-b border-black/8 text-[10px] font-bold tracking-widest uppercase text-[#64748B] sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2.5">Devis</th>
                  <th className="text-left px-3 py-2.5">Client</th>
                  <th className="text-right px-3 py-2.5">TTC</th>
                  {tab === "actifs" && <th className="text-right px-3 py-2.5">Proba</th>}
                  <th className="text-left px-3 py-2.5">État</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((p) => {
                  const st = STATUS_LABEL[p.status] || { l: p.status, c: "bg-slate-200" };
                  const total = (p.totals || {}).total_ttc || 0;
                  const isActive = active?.id === p.id;
                  const prob = Number(p.probability || 0);
                  const probColor = prob >= 90 ? "#16a34a" : prob >= 60 ? "#06B6D4" : prob >= 30 ? "#FFDCA8" : "#94a3b8";
                  return (
                    <tr key={p.id} onClick={() => setActive(p)} data-testid={`prop-${p.id}`} className={`border-b border-black/5 last:border-0 cursor-pointer transition-colors ${isActive ? "bg-[#06B6D4]/8" : "hover:bg-[#FAFAFA]"}`}>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <Link to={`/admin/devis/${p.id}`} onClick={(e) => e.stopPropagation()} data-testid={`prop-ref-link-${p.id}`} className="display font-extrabold text-[#0A1128] hover:text-[#06B6D4]">{p.ref}</Link>
                        </div>
                        <div className="text-[10px] text-[#64748B] mt-0.5 truncate max-w-[200px]">{p.project?.title || "—"}</div>
                        <div className="flex items-center gap-2 mt-1.5 text-[10px] text-[#64748B]">
                          <TinyEvent icon={Mail}      label="Env." date={fmtDate(p.sent_at)}        color="#06B6D4" />
                          <TinyEvent icon={MailOpen}  label={p.opened_count > 1 ? `Ouv.×${p.opened_count}` : "Ouv."} date={fmtDate(p.last_opened_at || p.opened_at)} color="#FFDCA8" />
                          <TinyEvent icon={PenSquare} label="Sig." date={fmtDate(p.signed_at)}      color="#16a34a" />
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs">
                        <div className="font-bold text-[#0A1128] truncate max-w-[140px]">{p.client?.name}</div>
                        {p.client?.company && <div className="text-[#64748B] truncate max-w-[140px]">{p.client.company}</div>}
                      </td>
                      <td className="px-3 py-3 text-right font-extrabold tabular-nums text-[#0A1128]">
                        {Number(total).toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €
                        {p.totals?.recurring_monthly_ttc > 0 && (
                          <div className="text-[10px] text-[#06B6D4] font-bold">+ {p.totals.recurring_monthly_ttc.toLocaleString("fr-FR")} €/m</div>
                        )}
                      </td>
                      {tab === "actifs" && (
                        <td className="px-3 py-3 text-right">
                          <span className="inline-flex items-center justify-center min-w-[36px] px-2 py-0.5 rounded-full text-[10px] font-extrabold" style={{ background: probColor, color: prob >= 60 ? "#fff" : "#0A1128" }}>
                            {prob}%
                          </span>
                        </td>
                      )}
                      <td className="px-3 py-3">
                        <Badge className={`${st.c} font-bold text-[10px]`}>{st.l}</Badge>
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex gap-1">
                          {/* Voir aperçu */}
                          <button onClick={(e) => { e.stopPropagation(); setActive(p); }} title="Voir l'aperçu" data-testid={`prop-view-${p.id}`} className="p-1.5 rounded hover:bg-black/5 text-[#0A1128]"><Eye size={13} /></button>
                          {/* Modifier (crayon) */}
                          <Link to={`/admin/devis/${p.id}`} onClick={(e) => e.stopPropagation()} title="Modifier" data-testid={`prop-edit-${p.id}`} className="p-1.5 rounded hover:bg-[#06B6D4]/15 text-[#06B6D4]"><PenSquare size={13} /></Link>
                          {/* Dupliquer */}
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            try { const r = await api.post(`/admin/proposals/${p.id}/duplicate`); toast.success(`Devis dupliqué : ${r.data.ref}`); load(); }
                            catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
                          }} title="Dupliquer ce devis" data-testid={`prop-duplicate-${p.id}`} className="p-1.5 rounded hover:bg-[#FFDCA8]/40 text-[#92400E]"><Copy size={13} /></button>
                          {/* Fermer (refuse) */}
                          {(p.status === "brouillon" || p.status === "envoye" || p.status === "negociation") && (
                            <button onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Fermer ce devis et le marquer comme refusé ?`)) return;
                              try { await api.post(`/admin/proposals/${p.id}/close`, { reason: "refuse" }); toast.success("Devis fermé"); load(); }
                              catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
                            }} title="Fermer / Refusé" data-testid={`prop-close-${p.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-500"><XIcon size={13} /></button>
                          )}
                          {/* Envoi TEST (aperçu) */}
                          {(p.status === "brouillon" || p.status === "envoye" || p.status === "negociation") && (
                            <button onClick={(e) => { e.stopPropagation(); openSend(p); }} title="Envoi TEST / Envoyer au client" data-testid={`prop-test-${p.id}`} className="p-1.5 rounded hover:bg-purple-50 text-purple-600"><Beaker size={13} /></button>
                          )}
                          {/* Envoyer au client (double confirm via modale) */}
                          {(p.status === "brouillon" || p.status === "envoye" || p.status === "negociation") && (
                            <button onClick={(e) => { e.stopPropagation(); openSend(p); }} title={p.status === "brouillon" ? "Envoyer au client" : "Renvoyer au client"} data-testid={`prop-send-${p.id}`} className="p-1.5 rounded hover:bg-green-50 text-green-600"><Send size={13} /></button>
                          )}
                          {p.status === "signe" && (
                            <button onClick={(e) => { e.stopPropagation(); markPaid(p, p.payment_terms === "100_upfront" ? "full" : "deposit"); }} title="Acompte reçu" className="p-1.5 rounded hover:bg-[#FFDCA8]/40 text-[#92400E]"><CreditCard size={13} /></button>
                          )}
                          {(p.status === "paye_acompte" || p.status === "en_cours" || p.status === "livre") && (
                            <button onClick={(e) => { e.stopPropagation(); markPaid(p, "balance"); }} title="Solde reçu" className="p-1.5 rounded hover:bg-[#A7F3D0]/40 text-[#065F46]"><CreditCard size={13} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Right: PDF preview */}
        <div className="bg-white border border-black/8 rounded-2xl overflow-hidden lg:sticky lg:top-4 lg:max-h-[calc(100vh-360px)]">
          {active ? (
            <>
              <div className="px-4 py-3 border-b border-black/8 bg-[#FAFAFA] flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0">
                  <div className="display font-extrabold text-[#0A1128] text-sm truncate">Aperçu · {active.ref}</div>
                  <div className="text-[11px] text-[#64748B] truncate">{active.client?.name} {active.client?.company ? `· ${active.client.company}` : ""}</div>
                </div>
                <div className="flex items-center gap-1">
                  {pdfUrl && (
                    <button onClick={() => {
                      const tok = localStorage.getItem("besson_admin_token") || "";
                      fetch(pdfUrl, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
                        .then((r) => r.blob())
                        .then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `${active.ref}.pdf`; a.click(); URL.revokeObjectURL(u); })
                        .catch(() => toast.error("Téléchargement échoué"));
                    }} className="inline-flex items-center gap-1 text-xs font-bold text-white bg-[#0A1128] hover:bg-[#14213d] px-2.5 py-1.5 rounded-md" data-testid="prop-download-pdf"><Download size={12} />PDF</button>
                  )}
                  {pdfUrl && <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#06B6D4] hover:underline px-1" title="Ouvrir dans un nouvel onglet"><ExternalLink size={12} /></a>}
                </div>
              </div>
              {pdfUrl ? (
                <object data={`${pdfUrl}#toolbar=0&navpanes=0`} type="application/pdf" className="w-full" style={{ height: "calc(100vh - 560px)", minHeight: 360 }}>
                  <iframe title={`PDF ${active.ref}`} src={pdfUrl} className="w-full" style={{ height: "calc(100vh - 560px)", minHeight: 360 }} />
                </object>
              ) : (
                <div className="p-10 text-center text-sm text-[#64748B]">Aperçu indisponible pour ce devis.</div>
              )}
              <EmailHistoryPanel docType="proposal" docId={active.id} docRef={active.ref} />
            </>
          ) : (
            <div className="p-10 text-center text-sm text-[#64748B]">Sélectionnez un devis pour afficher l'aperçu.</div>
          )}
        </div>
      </div>

      {sendingProp && (
        <ProposalSendModal
          proposal={sendingProp}
          onClose={() => setSendingProp(null)}
          onSent={() => load()}
        />
      )}
    </div>
  );
}

function StatCard({ tone, icon: Icon, label, value, sub, testId }) {
  const tones = {
    navy:  { bg: "bg-[#0A1128]", fg: "text-white",         iconBg: "bg-white/10",         iconFg: "text-[#67E8F9]" },
    cyan:  { bg: "bg-[#06B6D4]", fg: "text-white",         iconBg: "bg-white/15",         iconFg: "text-white"     },
    amber: { bg: "bg-[#FFDCA8]", fg: "text-[#92400E]",     iconBg: "bg-[#92400E]/15",     iconFg: "text-[#92400E]" },
    green: { bg: "bg-[#A7F3D0]", fg: "text-[#065F46]",     iconBg: "bg-[#065F46]/15",     iconFg: "text-[#065F46]" },
  }[tone] || { bg: "bg-white", fg: "text-[#0A1128]", iconBg: "bg-black/5", iconFg: "text-[#06B6D4]" };
  return (
    <div className={`${tones.bg} ${tones.fg} rounded-2xl p-4 shadow-sm relative overflow-hidden`} data-testid={testId}>
      <div className={`absolute top-3 right-3 w-8 h-8 rounded-lg ${tones.iconBg} flex items-center justify-center`}>
        <Icon size={16} className={tones.iconFg} />
      </div>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</div>
      <div className="display text-2xl sm:text-3xl font-extrabold mt-1.5 tabular-nums">{value}</div>
      <div className="text-[11px] mt-0.5 opacity-80">{sub}</div>
    </div>
  );
}

function TinyEvent({ icon: Icon, label, date, color }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      <Icon size={10} style={{ color: date ? color : "#cbd5e1" }} />
      <span className={date ? "text-[#0A1128] font-semibold" : "text-[#cbd5e1]"}>{label}</span>
      {date && <span className="text-[#94a3b8]">{date}</span>}
    </span>
  );
}
